import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Camera } from 'lucide-react';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { localizedName } from '@/lib/localized';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { SwitchRow } from '@/components/ui/switch';
import ImportDialog from './ImportDialog';
import { LocalizedInput, type LocalizedValue } from '@/components/ui/localized-input';
import { DragHandle, useReorderSensors } from '@/components/ui/reorder';
import { useTenantLocales } from '@/Hooks/useTenantLocales';
// Minor-unit arithmetic, because a size's stored `priceDelta` is derived by
// subtraction: 100.00 − 100.30 in floats is −0.30000000000000004, which
// `@IsNumber({ maxDecimalPlaces: 2 })` rejects outright.
import { fromMinor, toMinor } from '@/Pages/Billing/lib';
import { catalogItemBody } from './itemPayload';
import {
  CATALOG_ITEM_TYPES,
  type CatalogItem,
  type CatalogItemType,
  type Category,
  type ModifierGroup,
  type Station,
  type Variant,
  apiError,
  apiMoneyStep,
  isPreparable,
  nextBarcode,
} from './lib';

interface Props {
  categories: Category[];
  stations: Station[];
  onChanged: () => void;
  /**
   * The selected category, when something outside owns that choice — the
   * category tree beside this list does. Passing it makes the filter
   * controlled: the tab follows this value instead of keeping a competing one
   * of its own. `null`/`undefined` is "all categories", the standalone default.
   */
  categoryId?: string | null;
  /** Raised when the operator picks a category in the tab's own filter, so the
   *  owner of the selection can follow. */
  onCategoryChange?: (id: string) => void;
}

/**
 * One row of the sizes editor.
 *
 * `price` is the **full** price of the size — 100 for the small, 120 for the
 * large — which is how an operator thinks about a menu and what the till
 * prints. The model keeps a delta relative to `basePrice` instead, and that is
 * deliberate: `order-pricing.ts` applies an area price-list override to the
 * base *before* adding the delta, so a size holding an absolute price would
 * make every price list silently stop applying to any product that has sizes.
 * The conversion between the two happens here, on the way in and on the way
 * out, and nowhere else.
 *
 * A size exists on the server only once the item does, so the dialog edits
 * drafts and writes them in `submit`; `id` is null until then, and `key` is
 * what React lists on because a new row has no id to key by.
 */
interface SizeDraft {
  key: string;
  id: string | null;
  name: string;
  /** Absolute price, as typed. */
  price: string;
  isDefault: boolean;
}

let draftSeq = 0;

/** Rows per page. Shared by the query and the page arithmetic below. */
const PAGE_SIZE = 25;

/**
 * How many positions one `POST /catalog/reorder` may carry — the server's
 * `ReorderCatalogDto` cap, and the same 500 `BulkCatalogUpdateDto` uses.
 *
 * A drag inside a category is far under it, so a reorder is one request. The
 * split exists only for a whole-catalogue drag past the 500th product, which
 * would otherwise be a 400; each request is still one transaction server-side.
 */
const REORDER_BATCH = 500;

/** One item the server refused, with the reason it gave. */
/** The server caps a bulk body at 500 ids (`BulkCatalogUpdateDto`). */
const BULK_BATCH = 500;

/** What `PATCH|DELETE /catalog/bulk` answers: one row per id it was given. */
interface BulkResult {
  updated?: number;
  deleted?: number;
  results: { id: string; ok: boolean; reason?: string }[];
}

interface BulkFailure {
  id: string;
  /** Resolved at failure time — the row may be off screen by the time it shows. */
  name: string;
  reason: string;
}

/**
 * What a finished bulk run actually did.
 *
 * `ok + failures.length + skipped === total`. `skipped` is non-zero only when
 * the operator stopped the run, and those items were never sent — saying so is
 * the difference between "we did not do it" and "it failed".
 */
interface BulkReport {
  verb: 'update' | 'delete';
  ok: number;
  failures: BulkFailure[];
  skipped: number;
}

const BLANK = {
  /** §25 — one row per locale; the base locale is the item's own `name`. */
  names: {} as LocalizedValue,
  descriptions: {} as LocalizedValue,
  type: 'PRODUCT' as CatalogItemType,
  categoryId: '',
  stationId: '',
  sku: '',
  barcode: '',
  basePrice: '0',
  taxRate: '0',
  sortOrder: '0',
  isActive: true,
};

type FormState = typeof BLANK;

/** `GET /catalog/image-config` — whether this server can store a photo at all. */
interface ImageConfig {
  configured: boolean;
  maxBytes: number;
  accept: string[];
}

export default function ItemsTab({
  categories,
  stations,
  onChanged,
  categoryId: categoryIdProp,
  onCategoryChange,
}: Props) {
  const { t, i18n } = useTranslation();
  // Catalog prices are tenant-level data, so they are stated in the tenant's
  // base currency; a branch that trades in another one prices through a price
  // list, which carries its own `currency`.
  const { format: formatMoney, toMoney, decimals, code: currency } = useCurrency(null);
  // `basePrice` and `priceDelta` are both validated with
  // `@IsNumber({ maxDecimalPlaces: 2 })`, so the step never offers a third
  // decimal the API would reject — even where the currency has one.
  const moneyStep = apiMoneyStep(decimals);
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const { locales, defaultLocale } = useTenantLocales();

  const [rows, setRows] = useState<CatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  // Uncontrolled fallback: only consulted when no `categoryId` prop is given.
  const [ownCategoryId, setOwnCategoryId] = useState('');
  const controlledCategory = categoryIdProp !== undefined;
  const categoryId = controlledCategory ? (categoryIdProp ?? '') : ownCategoryId;
  const selectCategory = (id: string) => {
    // Keeping the local copy in step even while controlled would let the two
    // disagree the moment the owner declines a change; the prop is the value,
    // and the callback is the only way it moves.
    if (!controlledCategory) setOwnCategoryId(id);
    onCategoryChange?.(id);
  };
  const [stationId, setStationId] = useState('');
  /**
   * Which lifecycle states the list shows, defaulting to ACTIVE only.
   *
   * A replace-mode import retires the products it supersedes rather than
   * deleting them (§34 — orders, checks and stock movements point at those
   * rows). The list sent no `isActive` filter, so all 167 rows came back
   * together and the 41 retired size-suffixed products sat beside the 122 live
   * ones: "آيس أمريكانو" at 60 next to "آيس أمريكانو - كبير" at 80, both
   * looking equally sellable, and the header counting 165 products a venue does
   * not have. Retired rows are still reachable — they are history, not a
   * mistake — but they are no longer the default view.
   */
  const [soldOut, setSoldOut] = useState('');
  const [activeState, setActiveState] = useState('true');

  /**
   * The chosen items, keyed by id, holding the row itself and not just the id.
   *
   * Two reasons for the whole row. A confirm dialog has to name what is about
   * to happen ("Delete 12 products?" plus the first few names), and a failure
   * report has to name the item that was refused — both about items that may
   * no longer be on screen, because the selection deliberately survives paging
   * and filtering. An id-only set could only print ids.
   *
   * Surviving the filter is the choice made here rather than clearing: an
   * operator retiring a range picks a few from one category, then another few
   * from the next. What that costs is a count that can exceed what is visible,
   * so the bar states how many of the selected rows are not on this page.
   */
  const [selected, setSelected] = useState<Record<string, CatalogItem>>({});
  /** True while "select everything this filter matches" is paging the API. */
  const [selectingAll, setSelectingAll] = useState(false);
  /**
   * Live progress of a run: `null` when nothing is running. `waitSeconds` is
   * non-zero only while the run is sitting out the API's rate limit, which is
   * a minute of nothing happening if it is not said out loud.
   */
  const [bulk, setBulk] = useState<{ done: number; total: number; waitSeconds: number } | null>(
    null,
  );
  const [report, setReport] = useState<BulkReport | null>(null);
  /** Read inside the worker loop, so Stop takes effect on the next item rather
   *  than after a re-render. */
  const abortBulk = useRef(false);

  /** A drag has moved rows on this page and nothing has been written yet. */
  const [orderDirty, setOrderDirty] = useState(false);
  /** Read inside `load`, which must not depend on this to keep its identity. */
  const orderDirtyRef = useRef(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [form, setForm] = useState<FormState>(BLANK);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sizes and modifier groups are edited in the item dialog, on drafts that are
  // written once the item has an id — the same order `submit` already uses for
  // translations.
  const [sizes, setSizes] = useState<SizeDraft[]>([]);
  /** The server's own rows, kept for diffing on save. Empty while creating. */
  const [savedVariants, setSavedVariants] = useState<Variant[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [savedGroupIds, setSavedGroupIds] = useState<string[]>([]);
  const [allGroups, setAllGroups] = useState<ModifierGroup[]>([]);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [sizesOpen, setSizesOpen] = useState(false);

  // The product photo (S-15). Deliberately outside `FormState`: it is not a
  // field the item PATCH carries — the file goes to `POST /catalog/:id/image`
  // once the item has an id, the same order translations and sizes follow.
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageCleared, setImageCleared] = useState(false);
  /** `null` until the server answers; `configured: false` on an install with no
   *  bucket, which is most of them. */
  const [imageConfig, setImageConfig] = useState<ImageConfig | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);

  const canCreate = can('menu.create');
  // The import endpoint enforces `menu.update`, so the button mirrors that key
  // rather than `menu.create` — otherwise the gate either hides a button that
  // would have worked or offers one the server refuses.
  const canImport = can('menu.update');
  const canUpdate = can('menu.update');
  const canDelete = can('menu.delete');
  // Every bulk action is either `menu.update` (sold out, move, activate) or
  // `menu.delete` — the same keys the per-item routes on `CatalogController`
  // declare. With neither, there is nothing a selection could do, so the
  // checkbox column does not appear at all rather than offering ticks that
  // lead to a bar with no buttons in it.
  const canBulk = canUpdate || canDelete;

  // Locale rows are written by `PUT /catalog/:id/translations`, which enforces
  // `menu.update`. Offering the other languages to someone who only holds
  // `menu.create` promises a save the server refuses, so they get the base
  // locale — the one that rides along on the item itself.
  const translatableLocales = useMemo(
    () => (canUpdate ? locales : [defaultLocale]),
    [canUpdate, locales, defaultLocale],
  );
  const nameGiven = Object.values(form.names).some((value) => value.trim());

  // Every variant and modifier-group route on `CatalogController` is
  // `@RequirePermissions('menu.update')` — including on an item this user just
  // created. Showing the sections to someone who only holds `menu.create` would
  // promise writes the server refuses after the item is already saved.
  const canEditExtras = canUpdate;

  /**
   * The `basePrice` this item will actually be saved with.
   *
   * With sizes, the default size *is* the base price and every other size is
   * stored as its distance from it, so a price list still lands on the base and
   * still moves every size with it. Without sizes, the field the operator typed
   * into is the base price, exactly as before.
   */
  const effectiveBasePrice = useMemo(() => {
    if (!sizes.length) return form.basePrice;
    return (sizes.find((s) => s.isDefault) ?? sizes[0]).price;
  }, [sizes, form.basePrice]);
  /** A size the API would reject (`name` is @IsNotEmpty). Blocking the save is
   *  kinder than dropping the row: a half-typed size stays on screen. */
  const sizeUnnamed = sizes.some((s) => !s.name.trim());

  /**
   * The filter half of the list query, without the page.
   *
   * Shared, because "select everything this filter matches" has to ask for the
   * same set the table is showing. A second copy of these five lines is a
   * second chance for the two to drift, and the drift would be invisible: the
   * selection would quietly act on items the operator never saw.
   */
  const filters = useMemo(
    () => ({
      search: search || undefined,
      type: type || undefined,
      categoryId: categoryId || undefined,
      stationId: stationId || undefined,
      isSoldOut: soldOut === '' ? undefined : soldOut === 'true',
      isActive: activeState === '' ? undefined : activeState === 'true',
    }),
    [search, type, categoryId, stationId, soldOut, activeState],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: CatalogItem[]; meta?: { total: number } }>('/catalog', {
        params: { page, limit: PAGE_SIZE, ...filters },
      })
      .then(({ data }) => {
        const items = data.data ?? [];
        setRows(items);
        setTotal(data.meta?.total ?? items.length);
        // A page or filter change replaces the rows, which throws away a drag
        // that was never saved. Saying so is the difference between losing the
        // work and quietly pretending it never happened.
        if (orderDirtyRef.current) {
          orderDirtyRef.current = false;
          setOrderDirty(false);
          toast(t('catalog.items.reorder.discarded'));
        }
        // Refresh the snapshot of anything selected that is still in the list,
        // so a name changed since it was ticked is the name the confirm dialog
        // and the failure report use. Selections from another page keep the
        // copy they were made with — a stale name still identifies the row.
        setSelected((current) => {
          if (!Object.keys(current).length) return current;
          let changed = false;
          const next = { ...current };
          for (const item of items) {
            if (next[item.id] && next[item.id] !== item) {
              next[item.id] = item;
              changed = true;
            }
          }
          return changed ? next : current;
        });
      })
      .catch((e) => setError(apiError(e, t('catalog.items.loadFailed'))))
      .finally(() => setLoading(false));
  }, [page, filters, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  // A category chosen outside this tab — from the tree — has to land on the
  // first page, or page 4 of the old filter reads as an empty category.
  useEffect(() => {
    setPage(1);
  }, [categoryId]);

  // Whether object storage exists is a property of the deployment, so it is
  // asked once. Without it there is no upload control at all — a button whose
  // only possible answer is 503 is worse than no button.
  useEffect(() => {
    if (!canUpdate) return;
    let live = true;
    api
      .get<ImageConfig>('/catalog/image-config')
      .then(({ data }) => live && setImageConfig(data))
      .catch(() => live && setImageConfig({ configured: false, maxBytes: 0, accept: [] }));
    return () => {
      live = false;
    };
  }, [canUpdate]);

  /** A blob URL for the file the operator just picked, so the preview is
   *  immediate rather than waiting on a save and a round trip. */
  const imagePreview = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile],
  );
  useEffect(() => {
    if (!imagePreview) return;
    return () => URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  /** What the dialog shows: the pending file, else the saved photo unless it
   *  has been marked for removal. */
  const shownPhoto = imagePreview ?? (imageCleared ? null : imageUrl);

  const pickPhoto = (file: File | null) => {
    if (!file) return;
    // The same ceiling multer enforces, checked here so a 5 MB upload is
    // refused before it is sent rather than after.
    if (imageConfig?.maxBytes && file.size > imageConfig.maxBytes) {
      toast.error(
        t('catalog.items.photo.tooLarge', {
          size: Math.round(imageConfig.maxBytes / 1024 / 1024),
        }),
      );
      return;
    }
    setImageFile(file);
    setImageCleared(false);
  };

  /** Reset the sizes and modifier-group drafts; both dialogs start from here. */
  const resetExtras = () => {
    setSizes([]);
    setSavedVariants([]);
    setGroupIds([]);
    setSavedGroupIds([]);
    setSizesOpen(false);
  };

  /** The catalogue of groups that can be attached. Fetched once per opening. */
  const loadGroups = () => {
    if (!canEditExtras) return;
    api
      .get<{ data: ModifierGroup[] }>('/modifiers', { params: { limit: 200 } })
      .then(({ data }) => setAllGroups(data.data ?? []))
      .catch(() => setAllGroups([]));
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, names: {}, descriptions: {} });
    setImageUrl(null);
    setImageFile(null);
    setImageCleared(false);
    resetExtras();
    loadGroups();
    setFormOpen(true);
    // The next number in the venue's own run, asked of the server because only
    // it knows what is taken. The dialog opens immediately and the field fills
    // when the answer arrives — and stays editable either way, since a venue
    // that already prints its own labels has numbers of its own.
    void nextBarcode()
      .then((barcode) => setForm((current) => ({ ...current, barcode })))
      .catch(() => undefined);
  };

  const openEdit = (item: CatalogItem) => {
    setEditing(item);
    // The base locale is the item's own column; the rest are translation rows.
    const names: LocalizedValue = { [defaultLocale]: item.name };
    const descriptions: LocalizedValue = { [defaultLocale]: item.description ?? '' };
    for (const row of item.translations ?? []) {
      names[row.locale] = row.name;
      if (row.description) descriptions[row.locale] = row.description;
    }
    setForm({
      names,
      descriptions,
      type: item.type,
      categoryId: item.categoryId ?? '',
      stationId: item.stationId ?? '',
      sku: item.sku ?? '',
      barcode: item.barcode ?? '',
      basePrice: item.basePrice,
      taxRate: item.taxRate,
      sortOrder: String(item.sortOrder),
      isActive: item.isActive,
    });
    setImageUrl(item.imageUrl);
    setImageFile(null);
    setImageCleared(false);
    resetExtras();
    loadGroups();
    setFormOpen(true);

    // The list read carries only `_count`; the sizes and the attached groups
    // come from the single-item read, which is what it is for.
    if (!canEditExtras) return;
    setExtrasLoading(true);
    api
      .get<CatalogItem>(`/catalog/${item.id}`)
      .then(({ data }) => {
        const variants = data.variants ?? [];
        setSavedVariants(variants);
        // A row with no `isDefault` anywhere would otherwise have one forced on
        // it at save time and move the item's price; the zero-delta variant is
        // the one already sitting at the base price, so it is the safe pick.
        const fallback = variants.find((v) => Number(v.priceDelta) === 0) ?? variants[0];
        setSizes(
          variants.map((v) => ({
            key: v.id,
            id: v.id,
            name: v.name,
            price: fromMinor(
              toMinor(data.basePrice, currency) + toMinor(v.priceDelta, currency),
              currency,
            ),
            isDefault: v.isDefault || (!variants.some((x) => x.isDefault) && v.id === fallback?.id),
          })),
        );
        const attached = (data.modifierGroups ?? []).map((a) => a.groupId);
        setGroupIds(attached);
        setSavedGroupIds(attached);
        // Sizes stay folded on an item that has none, so the price and the
        // category stay at eye level; an item that already has sizes opens on
        // them rather than hiding what an operator configured earlier. Modifier
        // groups are always visible now — they have their own card.
        setSizesOpen(variants.length > 0);
      })
      .catch((e) => toast.error(apiError(e, t('catalog.items.loadFailed'))))
      .finally(() => setExtrasLoading(false));
  };

  const addSize = () => {
    // The first size inherits the item's current price, so giving a product a
    // size never silently reprices it.
    const seed = sizes.length ? effectiveBasePrice : form.basePrice;
    setSizes([
      ...sizes,
      {
        key: `new-${++draftSeq}`,
        id: null,
        name: '',
        price: seed,
        isDefault: sizes.length === 0,
      },
    ]);
    setSizesOpen(true);
  };

  const removeSize = (key: string) => {
    const row = sizes.find((s) => s.key === key);
    const rest = sizes.filter((s) => s.key !== key);
    // Losing the last size hands its price back to the base-price field, which
    // is about to reappear — the item keeps the price it was selling at.
    if (!rest.length && row) setForm((current) => ({ ...current, basePrice: row.price }));
    // The default must survive the row that held it, or the save would pick an
    // arbitrary one and shift every other size's delta.
    const orphaned = rest.length > 0 && !rest.some((s) => s.isDefault);
    setSizes(orphaned ? rest.map((s, i) => ({ ...s, isDefault: i === 0 })) : rest);
  };

  const patchSize = (key: string, patch: Partial<SizeDraft>) =>
    setSizes((current) => current.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const makeDefault = (key: string) =>
    setSizes((current) => current.map((s) => ({ ...s, isDefault: s.key === key })));

  /**
   * Write the sizes and group attachments now that the item has an id.
   *
   * Deltas are derived here, from the absolute prices the operator typed, in
   * minor units.
   */
  const saveExtras = async (itemId: string) => {
    const baseMinor = toMinor(toMoney(effectiveBasePrice), currency);
    const kept = new Set(sizes.map((s) => s.id).filter(Boolean) as string[]);

    for (const [index, size] of sizes.entries()) {
      const priceDelta = Number(
        fromMinor(toMinor(toMoney(size.price), currency) - baseMinor, currency),
      );
      const name = size.name.trim();
      const body = { name, priceDelta, isDefault: size.isDefault, sortOrder: index };
      const before = size.id ? savedVariants.find((v) => v.id === size.id) : undefined;
      if (!before) {
        await api.post(`/catalog/${itemId}/variants`, body);
        continue;
      }
      const unchanged =
        before.name === name &&
        Number(before.priceDelta) === priceDelta &&
        before.isDefault === size.isDefault &&
        before.sortOrder === index;
      if (!unchanged) await api.patch(`/catalog/${itemId}/variants/${size.id}`, body);
    }
    for (const variant of savedVariants) {
      if (!kept.has(variant.id)) await api.delete(`/catalog/${itemId}/variants/${variant.id}`);
    }

    for (const groupId of groupIds) {
      if (!savedGroupIds.includes(groupId)) {
        await api.post(`/catalog/${itemId}/modifier-groups`, { groupId });
      }
    }
    for (const groupId of savedGroupIds) {
      if (!groupIds.includes(groupId)) {
        await api.delete(`/catalog/${itemId}/modifier-groups/${groupId}`);
      }
    }
  };

  const submit = async () => {
    setSaving(true);
    // Numbers are sent as numbers because the DTO validates with @IsNumber;
    // they are never used for display arithmetic.
    // Falls back to whichever language was actually filled in, so an item is never nameless.
    const baseName =
      form.names[defaultLocale]?.trim() ||
      locales.map((code) => form.names[code]?.trim()).find(Boolean) ||
      '';
    const baseDescription = form.descriptions[defaultLocale]?.trim() || undefined;

    // Built from `CATALOG_ITEM_DTO_FIELDS`, never spread from form state:
    // `forbidNonWhitelisted` turns one undeclared property into a 400 on the
    // whole save, and this form carries several controls whose values are
    // deliberately *not* on the item body — sizes, translations and the photo
    // are separate requests below.
    //
    // `toMoney` clamps to the currency's minor unit; the DTO validates
    // `basePrice` with @IsNumber, so it goes back over the wire as a number.
    // With sizes on the item this is the default size's own price.
    const body = catalogItemBody({
      name: baseName,
      type: form.type,
      categoryId: form.categoryId,
      stationId: form.stationId,
      sku: form.sku,
      barcode: form.barcode,
      description: baseDescription,
      basePrice: toMoney(effectiveBasePrice),
      taxRate: form.taxRate,
      sortOrder: form.sortOrder,
      isActive: form.isActive,
    });
    try {
      const saved = editing
        ? ((await api.patch(`/catalog/${editing.id}`, body)).data as { data?: { id: string } })
        : ((await api.post('/catalog', body)).data as { data?: { id: string } });
      const itemId = editing?.id ?? saved?.data?.id ?? (saved as unknown as { id?: string })?.id;

      // §25 — every non-empty locale becomes a row on the same item. Sent after
      // the item exists, because a translation needs something to translate.
      const translations = locales
        .filter((code) => form.names[code]?.trim())
        .map((code) => ({
          locale: code,
          name: form.names[code].trim(),
          description: form.descriptions[code]?.trim() || undefined,
        }));
      // `PUT /catalog/:id/translations` enforces `menu.update`, which `POST
      // /catalog` does not — so a create-only user gets a 403 here on an item
      // that was already saved. `translatableLocales` keeps those rows off the
      // form for them; this mirrors the same key on the request.
      if (itemId && canUpdate && translations.length) {
        await api.put(`/catalog/${itemId}/translations`, { translations });
      }
      // Sizes and modifier groups are rows on the item too, and go the same
      // way: after the item exists, gated on the same key their routes are.
      if (itemId && canEditExtras) {
        await saveExtras(itemId);
      }

      // The photo is multipart and has its own route, so it goes last — and
      // only when something changed, so re-opening and re-saving a dialog does
      // not re-upload the same file. Its failure is reported on its own: the
      // item did save, and saying otherwise would send the operator back to
      // re-enter a form that is already stored.
      let photoError: string | null = null;
      if (itemId && canUpdate && (imageFile || (imageCleared && editing?.imageUrl))) {
        try {
          if (imageFile) {
            const upload = new FormData();
            upload.append('file', imageFile);
            await api.post(`/catalog/${itemId}/image`, upload);
          } else {
            await api.delete(`/catalog/${itemId}/image`);
          }
        } catch (e) {
          photoError = apiError(
            e,
            t('catalog.items.photo.failed'),
          );
        }
      }

      toast.success(t(editing ? 'catalog.items.updated' : 'catalog.items.created'));
      if (photoError) toast.error(photoError);
      setFormOpen(false);
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.items.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const toggleSoldOut = async (item: CatalogItem) => {
    const isSoldOut = !item.isSoldOut;
    try {
      await api.patch(`/catalog/${item.id}/sold-out`, { isSoldOut });
      toast.success(
        t(item.isSoldOut ? 'catalog.items.backInStock' : 'catalog.items.markedSoldOut'),
      );
      // The item form can be open on this very row, and its switch reads
      // `editing`. `load()` refreshes the table, not the dialog's copy.
      setEditing((current) => (current?.id === item.id ? { ...current, isSoldOut } : current));
      load();
    } catch (e) {
      toast.error(apiError(e, t('catalog.items.saveFailed')));
    }
  };

  const remove = async (item: CatalogItem) => {
    if (
      !(await confirm({
        title: t('catalog.items.deleteConfirm', { name: item.name }),
        tone: 'danger',
      }))
    )
      return;
    try {
      await api.delete(`/catalog/${item.id}`);
      toast.success(t('catalog.items.deleted'));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.items.saveFailed')));
    }
  };

  // --- selection & bulk actions ----------------------------------------------

  const selectedItems = useMemo(() => Object.values(selected), [selected]);
  const selectedCount = selectedItems.length;
  const shownSelected = rows.filter((item) => selected[item.id]).length;
  /** Selected but not in the table right now — another page, or filtered out. */
  const hiddenSelected = selectedCount - shownSelected;
  const allShownSelected = rows.length > 0 && shownSelected === rows.length;
  const running = bulk !== null;

  const toggleRow = (item: CatalogItem) =>
    setSelected((current) => {
      const next = { ...current };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });

  /** The header checkbox: it only ever means the rows on screen. */
  const toggleAllShown = () =>
    setSelected((current) => {
      const next = { ...current };
      if (allShownSelected) for (const item of rows) delete next[item.id];
      else for (const item of rows) next[item.id] = item;
      return next;
    });

  const clearSelection = () => {
    setSelected({});
    setReport(null);
  };

  /**
   * Tick everything the current filter matches, not just this page.
   *
   * Offered separately from the header checkbox and labelled with the real
   * total, because the two are different promises: one is "these 25", the
   * other is "all 122", and a checkbox that silently meant the second would
   * delete a hundred products the operator never looked at. Paged at the
   * `PaginationDto` ceiling of 200 so a large catalog is a couple of requests
   * rather than a couple of dozen.
   */
  const selectWholeFilter = async () => {
    setSelectingAll(true);
    try {
      const found: Record<string, CatalogItem> = {};
      for (let p = 1; ; p += 1) {
        const { data } = await api.get<{ data: CatalogItem[]; meta?: { pages?: number } }>(
          '/catalog',
          { params: { page: p, limit: 200, ...filters } },
        );
        for (const item of data.data ?? []) found[item.id] = item;
        if (p >= (data.meta?.pages ?? 1)) break;
      }
      setSelected((current) => ({ ...current, ...found }));
    } catch (e) {
      toast.error(apiError(e, t('catalog.items.loadFailed')));
    } finally {
      setSelectingAll(false);
    }
  };

  /** The first few names, for a confirm that says what it is about to touch. */
  const nameList = (items: CatalogItem[]) => {
    const separator = t('catalog.items.bulk.listSeparator');
    const shown = items.slice(0, 3).map((item) => localizedName(item, i18n.language));
    const rest = items.length - shown.length;
    const names = shown.join(separator);
    return rest > 0 ? t('catalog.items.bulk.andMore', { names, count: rest }) : names;
  };

  /**
   * Run `job` over a fixed list of items, a few at a time, and report honestly.
   *
   * Partial failure is the ordinary outcome, not the exception: one item in a
   * batch of forty can hit a SKU clash or a category that was deleted while
   * the operator was choosing, and the other thirty-nine will still go
   * through. So nothing here is all-or-nothing —
   *
   *  - every request is settled individually and its own server message kept;
   *  - only what actually succeeded leaves the selection, which leaves the
   *    refused ones ticked and the retry a single click on the same button;
   *  - the summary toast is an error whenever anything was lost, because a
   *    green "38 updated" over two silent refusals is the failure mode this
   *    whole screen exists to avoid.
   */
  /**
   * One request for the whole selection.
   *
   * `PATCH /catalog/bulk` and `DELETE /catalog/bulk` were built for exactly this
   * and were never called: this page sent one request PER PRODUCT through the
   * worker pool below, so a 127-item selection was 127 requests against a global
   * throttle of 120 per 60 seconds — measured at 112 updated, 55 refused, and a
   * minute of `Retry-After` backoff in the middle. The same bug the reorder route
   * fixed one screen over.
   *
   * The server caps a batch at 500, so a larger selection is chunked rather than
   * 400ing. Partial failure stays truthful: the endpoint answers per id, and a
   * batch that fails outright leaves the products after it counted as not
   * attempted rather than as refused.
   */
  const runBulkRequest = async (
    targets: CatalogItem[],
    patch: Record<string, unknown> | null,
    verb: 'update' | 'delete',
  ) => {
    abortBulk.current = false;
    setReport(null);
    setBulk({ done: 0, total: targets.length, waitSeconds: 0 });

    const byId = new Map(targets.map((item) => [item.id, item]));
    const failures: BulkFailure[] = [];
    const succeeded: string[] = [];

    for (let from = 0; from < targets.length; from += BULK_BATCH) {
      if (abortBulk.current) break;
      const batch = targets.slice(from, from + BULK_BATCH);
      const ids = batch.map((item) => item.id);
      try {
        const { data } = verb === 'delete'
          ? await api.delete<{ data: BulkResult }>('/catalog/bulk', { data: { ids } })
          : await api.patch<{ data: BulkResult }>('/catalog/bulk', { ids, patch });
        for (const row of data?.data?.results ?? []) {
          const item = byId.get(row.id);
          if (row.ok) succeeded.push(row.id);
          else
            failures.push({
              id: row.id,
              name: item ? localizedName(item, i18n.language) : row.id,
              reason: row.reason ?? t('catalog.items.saveFailed'),
            });
        }
      } catch (e) {
        // The whole batch was refused. Naming every product in it is honest —
        // none of them changed — and the batches after it were never attempted.
        for (const item of batch) {
          failures.push({
            id: item.id,
            name: localizedName(item, i18n.language),
            reason: apiError(e, t('catalog.items.saveFailed')),
          });
        }
      }
      setBulk((current) => (current ? { ...current, done: Math.min(from + batch.length, targets.length) } : current));
    }

    setBulk(null);
    setReport({
      verb,
      ok: succeeded.length,
      failures,
      skipped: targets.length - succeeded.length - failures.length,
    });
    if (succeeded.length) onChanged();
  };

  /** Confirm against a snapshot of the selection, then run it. */
  const askThenRun = async (
    title: string,
    label: string,
    tone: 'default' | 'danger',
    patch: Record<string, unknown> | null,
    verb: 'update' | 'delete',
  ) => {
    const targets = selectedItems;
    if (!targets.length || running) return;
    const ok = await confirm({
      title,
      body: t('catalog.items.bulk.confirmBody', { names: nameList(targets) }),
      confirmLabel: label,
      tone,
    });
    if (!ok) return;
    await runBulkRequest(targets, patch, verb);
  };

  const bulkSoldOut = (isSoldOut: boolean) =>
    askThenRun(
      t(isSoldOut ? 'catalog.items.bulk.confirmSoldOut' : 'catalog.items.bulk.confirmAvailable', {
        count: selectedCount,
      }),
      t(isSoldOut ? 'catalog.items.markSoldOut' : 'catalog.items.markAvailable'),
      'default',
      // The dedicated route rather than `PATCH /catalog/:id` with `isSoldOut`:
      // both write the same column, but only this one audits as
      // `catalog_item.sold_out`, so the day's stock-outs stay legible in the
      // audit log instead of looking like forty product edits.
      { isSoldOut },
      'update',
    );

  const bulkActive = (isActive: boolean) =>
    askThenRun(
      t(isActive ? 'catalog.items.bulk.confirmActivate' : 'catalog.items.bulk.confirmDeactivate', {
        count: selectedCount,
      }),
      t(isActive ? 'catalog.items.bulk.activate' : 'catalog.items.bulk.deactivate'),
      isActive ? 'default' : 'danger',
      { isActive },
      'update',
    );

  const bulkMove = (targetCategoryId: string) => {
    const category = categories.find((c) => c.id === targetCategoryId);
    return askThenRun(
      t('catalog.items.bulk.confirmMove', {
        count: selectedCount,
        category: category ? localizedName(category, i18n.language) : '',
      }),
      t('catalog.items.bulk.move'),
      'default',
      { categoryId: targetCategoryId },
      'update',
    );
  };

  const bulkDelete = () =>
    askThenRun(
      t('catalog.items.bulk.confirmDelete', { count: selectedCount }),
      t('common.delete'),
      'danger',
      null,
      'delete',
    );

  // --- ordering --------------------------------------------------------------

  /**
   * The filters that make a drag meaningless, and why.
   *
   * `sortOrder` is one number per product, read by the guest menu as the order
   * of a whole category. A drag inside a *narrowed* view — a search, one
   * station, only the sold-out ones — puts item A above item B while saying
   * nothing about the twenty products between them that the filter hid, and
   * whatever is written next has to invent an order for those. So the only
   * views that can be dragged are the ones that show a complete order: one
   * category, or the whole catalogue. Choosing a category is the normal case
   * and the one the operator asked for; everything else says why it is off
   * rather than presenting a handle that does nothing.
   */
  // Active-only is the DEFAULT view and the one whose order actually matters —
  // a retired product is not sellable, so its position is irrelevant. Dragging
  // stays enabled there and is withdrawn only when retired rows are on screen,
  // where a saved order would renumber rows nobody can sell.
  const narrowingFilters = Boolean(search || type || stationId || soldOut || activeState !== 'true');
  const canReorder = canUpdate && !narrowingFilters;
  const draggableRows = canReorder && !running && !savingOrder && rows.length > 1;
  const orderedCategory = categoryId ? categories.find((c) => c.id === categoryId) : null;

  const sensors = useReorderSensors();

  /**
   * What a screen reader hears while a row moves. dnd-kit's own announcements
   * are English strings inside the library, so on an Arabic dashboard they
   * would be the one part of this screen that never translates. The position is
   * stated against the page, which is the set the drag can actually rearrange.
   */
  const accessibility = useMemo(() => {
    const say = (key: string, id: string | number) => {
      const index = rows.findIndex((item) => item.id === String(id));
      if (index < 0) return undefined;
      return t(key, {
        name: localizedName(rows[index], i18n.language),
        index: index + 1,
        count: rows.length,
      });
    };
    return {
      screenReaderInstructions: { draggable: t('catalog.reorder.instructions') },
      announcements: {
        onDragStart: ({ active }: { active: { id: string | number } }) =>
          say('catalog.reorder.picked', active.id),
        onDragOver: ({ over }: { over: { id: string | number } | null }) =>
          over ? say('catalog.reorder.movedOver', over.id) : undefined,
        onDragEnd: ({ active }: { active: { id: string | number } }) =>
          say('catalog.reorder.dropped', active.id),
        onDragCancel: ({ active }: { active: { id: string | number } }) =>
          say('catalog.reorder.cancelled', active.id),
      },
    };
  }, [rows, t, i18n.language]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((item) => item.id === active.id);
    const to = rows.findIndex((item) => item.id === over.id);
    if (from < 0 || to < 0) return;
    setRows(arrayMove(rows, from, to));
    orderDirtyRef.current = true;
    setOrderDirty(true);
  };

  const discardOrder = () => {
    orderDirtyRef.current = false;
    setOrderDirty(false);
    load();
  };

  /**
   * Persist the order — one `POST /catalog/reorder`, whatever moved.
   *
   * This was one `PATCH /catalog/:id` per moved product, because
   * `CatalogController` had no bulk reorder route: a measured 31 requests for a
   * single drag, against a global throttle of 120 per 60 seconds, so reordering
   * a large category tripped it and left half an ordering behind. The route now
   * mirrors `POST /categories/reorder` and writes the whole thing in one
   * transaction, so a reorder no longer half-lands — it is applied or it is
   * refused, and either way the report says which.
   *
   * The page is 25 rows of a set that may be 122, so the write cannot be
   * "renumber what is on screen": that would leave the other pages holding
   * numbers from the old sequence and interleave the two. Instead the whole
   * filtered set is read back in its current server order, the dragged page is
   * dropped into the positions it already occupied, and the result is numbered
   * 0..n-1. Rows on other pages keep their relative order and only move if the
   * drag genuinely moved something past them.
   */
  const saveOrder = async () => {
    if (!orderDirty || savingOrder || running) return;
    setSavingOrder(true);
    try {
      const whole: CatalogItem[] = [];
      for (let p = 1; ; p += 1) {
        const { data } = await api.get<{ data: CatalogItem[]; meta?: { pages?: number } }>(
          '/catalog',
          { params: { page: p, limit: 200, ...filters } },
        );
        whole.push(...(data.data ?? []));
        if (p >= (data.meta?.pages ?? 1)) break;
      }

      const onPage = new Set(rows.map((item) => item.id));
      const slots = whole.reduce<number[]>((acc, item, index) => {
        if (onPage.has(item.id)) acc.push(index);
        return acc;
      }, []);
      // Somebody else added or removed a product between the page load and the
      // save. Guessing where the missing rows went would write an order nobody
      // asked for, so the drag is dropped and the fresh list shown instead.
      if (slots.length !== rows.length) {
        toast.error(t('catalog.items.reorder.movedUnderYou'));
        discardOrder();
        return;
      }

      const fresh = new Map(whole.map((item) => [item.id, item]));
      const next = [...whole];
      slots.forEach((slot, i) => {
        next[slot] = fresh.get(rows[i].id) ?? rows[i];
      });

      const moves = next
        .map((item, index) => ({ item, index }))
        .filter(({ item, index }) => item.sortOrder !== index);
      if (!moves.length) {
        orderDirtyRef.current = false;
        setOrderDirty(false);
        toast(t('catalog.items.reorder.nothingMoved'));
        return;
      }

      const ok = await confirm({
        title: t('catalog.items.reorder.confirm', { count: moves.length }),
        body: t('catalog.items.reorder.confirmBody', { count: moves.length }),
        confirmLabel: t('catalog.items.reorder.save'),
      });
      if (!ok) return;

      const ordering = moves.map(({ item, index }) => ({ id: item.id, sortOrder: index }));
      setReport(null);
      let written = 0;

      for (let start = 0; start < ordering.length; start += REORDER_BATCH) {
        const batch = ordering.slice(start, start + REORDER_BATCH);
        try {
          await api.post('/catalog/reorder', { items: batch });
          written += batch.length;
        } catch (e) {
          // The request is all-or-nothing, so every product in this batch kept
          // the position it had, and anything after it was never sent. Calling
          // that "saved" is the failure mode this screen exists to avoid.
          const reason = apiError(e, t('catalog.items.saveFailed'));
          const failures = moves.slice(start, start + batch.length).map(({ item }) => ({
            id: item.id,
            name: localizedName(item, i18n.language),
            reason,
          }));
          setReport({
            verb: 'update',
            ok: written,
            failures,
            skipped: ordering.length - written - failures.length,
          });
          toast.error(
            t('catalog.items.bulk.updatePartial', { ok: written, failed: failures.length }),
          );
          // If nothing reached the server the rows on screen are still exactly
          // what was dragged, so the drag stays pending and Save is one more
          // click. Once a batch has landed the server holds a mixture, and the
          // only honest thing to show is what it actually holds.
          if (written > 0) {
            orderDirtyRef.current = false;
            setOrderDirty(false);
            load();
            onChanged();
          }
          return;
        }
      }

      orderDirtyRef.current = false;
      setOrderDirty(false);
      setReport({ verb: 'update', ok: written, failures: [], skipped: 0 });
      toast.success(t('catalog.items.bulk.updateDone', { count: written }));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.items.reorder.readFailed')));
    } finally {
      setSavingOrder(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const attachableGroups = allGroups.filter((g) => !groupIds.includes(g.id));
  const attachedGroups = groupIds.map(
    (id) =>
      allGroups.find((g) => g.id === id) ?? {
        id,
        name: id,
        minSelect: 0,
        maxSelect: 0,
        isRequired: false,
        sortOrder: 0,
      },
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-56"
          placeholder={t('catalog.items.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <Select
          className="w-44"
          value={type}
          onChange={(e) => {
            setPage(1);
            setType(e.target.value);
          }}
          aria-label={t('catalog.items.type')}
        >
          <option value="">{t('catalog.items.allTypes')}</option>
          {CATALOG_ITEM_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`catalog.itemType.${value}`)}
            </option>
          ))}
        </Select>
        <Select
          className="w-44"
          value={categoryId}
          onChange={(e) => {
            setPage(1);
            selectCategory(e.target.value);
          }}
          aria-label={t('catalog.items.category')}
        >
          <option value="">{t('catalog.items.allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="w-44"
          value={stationId}
          onChange={(e) => {
            setPage(1);
            setStationId(e.target.value);
          }}
          aria-label={t('catalog.items.station')}
        >
          <option value="">{t('catalog.items.allStations')}</option>
          {/*
            S-32 — the products the kitchen will never see.
            A product with no station is dropped by the send path: the order is
            accepted and no ticket is made. Some are stationless on purpose (the
            syrups, an hour of PS5), so this is a question to ask rather than a
            warning to show — but it was not askable at all before, and that is
            how 120 products came to be unrouted without anyone noticing.
          */}
          <option value="none">{t('catalog.items.noStationFilter')}</option>
          {stations.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="w-40"
          value={soldOut}
          onChange={(e) => {
            setPage(1);
            setSoldOut(e.target.value);
          }}
          aria-label={t('catalog.items.soldOut')}
        >
          <option value="">{t('catalog.items.allStock')}</option>
          <option value="true">{t('catalog.items.onlySoldOut')}</option>
          <option value="false">{t('catalog.items.onlyAvailable')}</option>
        </Select>
        <Select
          className="w-44"
          value={activeState}
          onChange={(e) => {
            setPage(1);
            setActiveState(e.target.value);
          }}
          aria-label={t('catalog.items.stateFilter')}
        >
          <option value="true">{t('catalog.items.onlyActive')}</option>
          <option value="false">{t('catalog.items.onlyRetired')}</option>
          <option value="">{t('catalog.items.allStates')}</option>
        </Select>

        <div className="ms-auto">
          {canImport && (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              {t('catalog.import.title')}
            </Button>
          )}
          {canCreate && <Button onClick={openCreate}>{t('catalog.items.newItem')}</Button>}
        </div>
      </div>

      {canBulk && selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-accent-line bg-accent-soft p-3">
          <span className="text-sm font-medium">
            {t('catalog.items.bulk.selected', { count: selectedCount })}
            {hiddenSelected > 0 && (
              <span className="ms-2 font-normal text-ink-muted">
                {t('catalog.items.bulk.notShown', { count: hiddenSelected })}
              </span>
            )}
          </span>

          {bulk ? (
            <>
              {/* A 122-item run that looks frozen gets clicked a second time,
                  so the buttons are gone for the duration and what replaces
                  them is a moving count. */}
              <div className="h-2 w-40 overflow-hidden rounded-full bg-accent-line">
                <div
                  className="h-full rounded-full bg-accent-solid transition-[width] duration-150"
                  style={{ width: `${Math.round((bulk.done / Math.max(1, bulk.total)) * 100)}%` }}
                />
              </div>
              <span aria-live="polite" className="text-sm tabular-nums">
                {t('catalog.items.bulk.progress', { done: bulk.done, total: bulk.total })}
                {bulk.waitSeconds > 0 && (
                  <span className="ms-2 font-normal text-ink-muted">
                    {t('catalog.items.bulk.rateLimited', { seconds: bulk.waitSeconds })}
                  </span>
                )}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="ms-auto"
                onClick={() => {
                  abortBulk.current = true;
                }}
              >
                {t('catalog.items.bulk.stop')}
              </Button>
            </>
          ) : (
            <>
              {canUpdate && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => bulkSoldOut(true)}>
                    {t('catalog.items.markSoldOut')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => bulkSoldOut(false)}>
                    {t('catalog.items.markAvailable')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => bulkActive(true)}>
                    {t('catalog.items.bulk.activate')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => bulkActive(false)}>
                    {t('catalog.items.bulk.deactivate')}
                  </Button>
                  <Select
                    className="h-9 w-52"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) void bulkMove(e.target.value);
                    }}
                    aria-label={t('catalog.items.bulk.moveTo')}
                  >
                    <option value="">{t('catalog.items.bulk.moveTo')}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {localizedName(c, i18n.language)}
                      </option>
                    ))}
                  </Select>
                </>
              )}
              {canDelete && (
                <Button size="sm" variant="danger" onClick={bulkDelete}>
                  {t('common.delete')}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="ms-auto" onClick={clearSelection}>
                {t('catalog.items.bulk.clear')}
              </Button>
            </>
          )}
        </div>
      )}

      {/* The header checkbox took the page; this is the only thing that takes
          the whole filter, and it says the number out loud before it does. */}
      {canBulk && !running && allShownSelected && total > rows.length && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-subtle px-3 py-2 text-sm">
          <span>{t('catalog.items.bulk.pageSelected', { count: rows.length })}</span>
          <Button size="sm" variant="ghost" disabled={selectingAll} onClick={selectWholeFilter}>
            {selectingAll
              ? t('common.loading')
              : t('catalog.items.bulk.selectAllMatching', { count: total })}
          </Button>
        </div>
      )}

      {report && (
        <div
          role="status"
          className={`space-y-2 rounded-xl border p-3 text-sm ${
            report.failures.length ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/50' : 'border-line bg-subtle'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium">
              {report.failures.length > 0
                ? t(
                    report.verb === 'delete'
                      ? 'catalog.items.bulk.deletePartial'
                      : 'catalog.items.bulk.updatePartial',
                    { ok: report.ok, failed: report.failures.length },
                  )
                : t(
                    report.verb === 'delete'
                      ? 'catalog.items.bulk.deleteDone'
                      : 'catalog.items.bulk.updateDone',
                    { count: report.ok },
                  )}
              {report.skipped > 0 &&
                ` · ${t('catalog.items.bulk.notAttempted', { count: report.skipped })}`}
            </p>
            <Button size="sm" variant="ghost" onClick={() => setReport(null)}>
              {t('catalog.items.bulk.dismiss')}
            </Button>
          </div>
          {report.failures.length > 0 && (
            <>
              <p className="text-xs text-ink-muted">
                {t('catalog.items.bulk.failuresHint')}
              </p>
              <ul className="space-y-1">
                {report.failures.map((failure) => (
                  <li key={failure.id} className="flex flex-wrap gap-x-2">
                    <span className="font-medium">{failure.name}</span>
                    <span className="text-ink-muted">{failure.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* What a drag on this page actually promises. The list is 25 rows of a
          set that may be five times that, so the scope is stated before the
          handles are offered rather than left to be assumed. */}
      {canUpdate && !loading && !error && rows.length > 1 && (
        <div
          className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm ${
            orderDirty ? 'border border-accent-line bg-accent-soft' : 'bg-subtle'
          }`}
        >
          <span className={orderDirty ? 'font-medium' : 'text-ink-muted'}>
            {!canReorder
              ? t('catalog.items.reorder.blockedByFilters')
              : orderDirty
                ? t('catalog.items.reorder.dirty')
                : orderedCategory
                  ? t('catalog.items.reorder.scopeCategory', {
                      count: total,
                      category: localizedName(orderedCategory, i18n.language),
                    })
                  : t('catalog.items.reorder.scopeAll', { count: total })}
          </span>
          {orderDirty && (
            <div className="ms-auto flex items-center gap-2">
              <Button size="sm" variant="ghost" disabled={savingOrder} onClick={discardOrder}>
                {t('catalog.items.reorder.discard')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={savingOrder || running}
                onClick={saveOrder}
              >
                {t('catalog.items.reorder.save')}
              </Button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('catalog.items.empty')} hint={t('catalog.items.emptyHint')} />
      ) : (
        <TableWrap>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            accessibility={accessibility}
            onDragEnd={onDragEnd}
          >
            <Table>
              <THead>
                <tr>
                  {canUpdate && (
                    <TH className="w-10">
                      <span className="sr-only">{t('catalog.items.reorder.column')}</span>
                    </TH>
                  )}
                  {canBulk && (
                    <TH className="w-10">
                      <CheckBox
                        checked={allShownSelected}
                        indeterminate={shownSelected > 0 && !allShownSelected}
                        disabled={running}
                        onChange={toggleAllShown}
                        label={t('catalog.items.bulk.selectPage', { count: rows.length })}
                      />
                    </TH>
                  )}
                  <TH>{t('catalog.items.name')}</TH>
                  <TH>{t('catalog.items.type')}</TH>
                  <TH>{t('catalog.items.category')}</TH>
                  <TH>{t('catalog.items.basePrice')}</TH>
                  <TH>{t('catalog.items.state')}</TH>
                  <TH className="text-end">{t('catalog.items.actions')}</TH>
                </tr>
              </THead>
              <tbody>
                <SortableContext
                  items={rows.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {rows.map((item) => (
                    <SortableItemRow
                      key={item.id}
                      id={item.id}
                      draggable={draggableRows}
                      handleLabel={t('catalog.items.reorder.handle', {
                        name: localizedName(item, i18n.language),
                      })}
                      className={selected[item.id] ? 'bg-accent-soft' : undefined}
                    >
                      {(handle) => (
                        <>
                          {canUpdate && <TD className="align-middle">{handle}</TD>}
                          {canBulk && (
                            <TD>
                              <CheckBox
                                checked={!!selected[item.id]}
                                disabled={running}
                                onChange={() => toggleRow(item)}
                                label={localizedName(item, i18n.language)}
                              />
                            </TD>
                          )}
                          <TD>
                            <div className="flex items-start gap-2">
                              {item.imageUrl && (
                                <img
                                  src={item.imageUrl}
                                  alt=""
                                  loading="lazy"
                                  className="mt-0.5 h-9 w-9 shrink-0 rounded-md object-cover"
                                />
                              )}
                              <div className="min-w-0">
                                <div className="font-medium">
                                  {localizedName(item, i18n.language)}
                                </div>
                                <div className="text-xs text-ink-muted">
                              {item.sku ? `${t('catalog.items.sku')}: ${item.sku}` : '—'}
                              {item.translations?.length
                                ? ` · ${t('catalog.items.localeCount', { count: item.translations.length })}`
                                : ''}
                              {item._count?.variants
                                ? ` · ${t('catalog.items.sizeCount', { count: item._count.variants })}`
                                : ''}
                              {item._count?.modifierGroups
                                ? ` · ${t('catalog.items.groupCount', { count: item._count.modifierGroups })}`
                                : ''}
                                </div>
                              </div>
                            </div>
                          </TD>
                          <TD>{t(`catalog.itemType.${item.type}`)}</TD>
                          <TD>{item.category?.name ?? '—'}</TD>
                          <TD>
                            {item._count?.variants
                              ? t('catalog.items.priceFrom', { price: formatMoney(item.basePrice) })
                              : formatMoney(item.basePrice)}
                          </TD>
                          <TD>
                            <div className="flex flex-wrap gap-1">
                              <Badge tone={item.isActive ? 'success' : 'neutral'}>
                                {t(
                                  item.isActive ? 'catalog.items.active' : 'catalog.items.inactive',
                                )}
                              </Badge>
                              {item.isSoldOut && (
                                <Badge tone="danger">{t('catalog.items.soldOut')}</Badge>
                              )}
                            </div>
                          </TD>
                          <TD className="text-end">
                            <div className="flex flex-wrap justify-end gap-2">
                              {canUpdate && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={running}
                                  onClick={() => toggleSoldOut(item)}
                                >
                                  {t(
                                    item.isSoldOut
                                      ? 'catalog.items.markAvailable'
                                      : 'catalog.items.markSoldOut',
                                  )}
                                </Button>
                              )}
                              {canUpdate && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={running}
                                  onClick={() => openEdit(item)}
                                >
                                  {t('common.edit')}
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={running}
                                  onClick={() => remove(item)}
                                >
                                  {t('common.delete')}
                                </Button>
                              )}
                            </div>
                          </TD>
                        </>
                      )}
                    </SortableItemRow>
                  ))}
                </SortableContext>
              </tbody>
            </Table>
          </DndContext>
        </TableWrap>
      )}

      {pages > 1 && (
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t('common.back')}
          </Button>
          <span className="text-xs text-ink-muted">
            {t('catalog.pageOf', { page, pages, total })}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            {t('common.next')}
          </Button>
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'catalog.items.editItem' : 'catalog.items.newItem')}
        description={t('catalog.items.formHint')}
        // Wider than the rest of the app's dialogs because this form is now the
        // two-column shape 64:205 draws: the product on the left, everything
        // attached to it — photo, modifier groups, availability — on the right.
        className="max-w-5xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            {/* One primary, not the drawn "Save as draft" / "Publish" pair.
                `CatalogItem` has `isActive` and `isSoldOut` and no third state;
                both buttons would call the same route, and "draft" would be a
                new word for the Active switch two cards away. */}
            <Button
              onClick={submit}
              // `CreateCatalogItemDto.name` is @IsNotEmpty, and `submit` derives
              // that name from whichever locale was filled in.
              disabled={saving || !nameGiven || sizeUnnamed}
              title={
                nameGiven
                  ? sizeUnnamed
                    ? t('catalog.items.sizeNameRequired')
                    : undefined
                  : t('catalog.items.nameRequired')
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
          <div className="space-y-4">
            {/* §25 — both of these are guest-facing and both translate.
                `CatalogTranslation` and `PUT /catalog/:id/translations` exist,
                and the description is the copy that reaches the QR menu, so a
                single-language field would strip Arabic from every product a
                tenant creates. */}
            <Field
              label={t('catalog.items.name')}
              required
              hint={canUpdate ? undefined : t('catalog.items.baseLocaleOnly')}
            >
              <LocalizedInput
                value={form.names}
                onChange={(names) => setForm({ ...form, names })}
                locales={translatableLocales}
                autoFocus
              />
            </Field>

            <Field label={t('catalog.items.description')}>
              <LocalizedInput
                value={form.descriptions}
                onChange={(descriptions) => setForm({ ...form, descriptions })}
                locales={translatableLocales}
                multiline
              />
            </Field>

            <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2">
              <Field label={t('catalog.items.type')}>
                <Select
                  value={form.type}
                  onChange={(e) => {
                    const type = e.target.value as CatalogItemType;
                    // A station left over from a previous type would keep printing
                    // tickets for something nobody prepares.
                    setForm({ ...form, type, stationId: isPreparable(type) ? form.stationId : '' });
                  }}
                >
                  {CATALOG_ITEM_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`catalog.itemType.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Not required, whatever the frame's asterisk says:
                  `CreateCatalogItemDto.categoryId` is @IsOptional and the column
                  is nullable, so a client-only star would block a legal save. */}
              <Field label={t('catalog.items.category')}>
                <Select
                  value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                >
                  <option value="">{t('catalog.items.noCategory')}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Not "Auto-generated": there is no SKU generator anywhere.
                  `GET /catalog/next-barcode` makes a *barcode*, which is the
                  separate field below. */}
              <Field label={t('catalog.items.sku')}>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  placeholder={t('catalog.items.skuHint')}
                />
              </Field>

              <Field label={t('catalog.items.barcode')} hint={t('catalog.items.barcodeHint')}>
                <div className="flex gap-2">
                  <Input
                    value={form.barcode}
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    className="flex-1 tabular-nums"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void nextBarcode().then((barcode) => setForm((c) => ({ ...c, barcode })))
                    }
                  >
                    {t('catalog.items.regenerate')}
                  </Button>
                </div>
              </Field>
            </div>

            <FormSection title={t('catalog.items.pricing')}>
              <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-3">
                {/* With sizes on the item there is no single price to state here —
                    each size carries its own, and the default one is what gets
                    stored as the base. Leaving this field visible would show a
                    number that contradicts the sizes below it. */}
                {sizes.length === 0 && (
                  <Field label={t('catalog.items.price')}>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step={moneyStep}
                      min="0"
                      value={form.basePrice}
                      onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
                    />
                  </Field>
                )}
                {/* Not a "Tax group": no tax entity exists in the product — no
                    model, no enum, no reusable rate. What exists is this
                    per-item fraction, and its hint says so. */}
                <Field label={t('catalog.items.taxRate')} hint={t('catalog.items.taxRateHint')}>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    max="1"
                    value={form.taxRate}
                    onChange={(e) => setForm({ ...form, taxRate: e.target.value })}
                  />
                </Field>
                <Field label={t('catalog.items.sortOrder')}>
                  <Input
                    type="number"
                    min="0"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  />
                </Field>
              </div>

              {canEditExtras && (
                <Section
                  title={t('catalog.items.sizes')}
                  summary={
                    sizes.length
                      ? t('catalog.items.sizeCount', { count: sizes.length })
                      : t('catalog.items.noSizes')
                  }
                  open={sizesOpen}
                  onToggle={() => setSizesOpen(!sizesOpen)}
                >
                  <p className="text-xs text-ink-muted">{t('catalog.items.sizesHint')}</p>
                  {sizes.map((size) => (
                    <div key={size.key} className="flex flex-wrap items-center gap-2">
                      <Input
                        className="w-40"
                        placeholder={t('catalog.items.sizeName')}
                        value={size.name}
                        onChange={(e) => patchSize(size.key, { name: e.target.value })}
                        aria-label={t('catalog.items.sizeName')}
                      />
                      <Input
                        className="w-32 tabular-nums"
                        type="number"
                        inputMode="decimal"
                        step={moneyStep}
                        min="0"
                        value={size.price}
                        onChange={(e) => patchSize(size.key, { price: e.target.value })}
                        aria-label={t('catalog.items.sizePrice')}
                      />
                      <span className="text-xs tabular-nums text-ink-muted">
                        {formatMoney(size.price)}
                      </span>
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="radio"
                          name="catalog-default-size"
                          checked={size.isDefault}
                          onChange={() => makeDefault(size.key)}
                        />
                        {t('catalog.items.defaultSize')}
                      </label>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ms-auto"
                        onClick={() => removeSize(size.key)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="secondary" onClick={addSize}>
                    {t('catalog.items.addSize')}
                  </Button>
                </Section>
              )}
            </FormSection>

            <FormSection
              title={t('catalog.items.kitchenRouting')}
            >
              {/* Station and nothing else. "Prep time (min)" has no column, no
                  DTO field and no consumer: the only prep timing in the product
                  is `prepSeconds`, an analytics measure over what the kitchen
                  actually did. */}
              <Field
                label={t('catalog.items.station')}
                hint={
                  isPreparable(form.type)
                    ? t('catalog.items.stationHint')
                    : t('catalog.items.stationNotPrepared')
                }
              >
                <Select
                  value={form.stationId}
                  disabled={!isPreparable(form.type)}
                  onChange={(e) => setForm({ ...form, stationId: e.target.value })}
                >
                  <option value="">{t('catalog.items.noStation')}</option>
                  {stations.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </FormSection>
          </div>

          <div className="space-y-4">
            {extrasLoading && <LoadingState label={t('common.loading')} />}

            {(imageConfig?.configured || shownPhoto) && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('catalog.items.photo.label')}</CardTitle>
                  <CardDescription>
                    {imageConfig?.configured
                      ? t('catalog.items.photo.hint')
                      : t('catalog.items.photo.unavailable')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {shownPhoto ? (
                    <img
                      src={shownPhoto}
                      alt=""
                      className="h-40 w-full rounded-lg border border-line object-cover"
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={!imageConfig?.configured}
                      onClick={() => imageInput.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        pickPhoto(e.dataTransfer.files?.[0] ?? null);
                      }}
                      className="flex h-40 w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line-strong text-center disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Icon as={Camera} size="lg" className="text-accent" aria-hidden />
                      <span className="text-sm font-medium text-accent">
                        {t('catalog.items.photo.choose')}
                      </span>
                      {/* Both halves come from the server: the MIME list from
                          `GET /catalog/image-config` and the ceiling from
                          `STORAGE_MAX_UPLOAD_BYTES`, which is env-overridable.
                          The frame's "JPG, PNG up to 5MB" hard-codes two values
                          that are not this client's to know. */}
                      {imageConfig?.configured && (
                        <span className="text-xs text-ink-muted">
                          {t('catalog.items.photo.limits', {
                            types: imageConfig.accept
                              .map((mime) => mime.split('/')[1]?.toUpperCase() ?? mime)
                              .join(', '),
                            size: Math.round(imageConfig.maxBytes / 1024 / 1024),
                          })}
                        </span>
                      )}
                    </button>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {imageConfig?.configured && (
                      <>
                        <input
                          ref={imageInput}
                          type="file"
                          className="hidden"
                          accept={imageConfig.accept.join(',')}
                          onChange={(e) => {
                            pickPhoto(e.target.files?.[0] ?? null);
                            // Cleared so picking the same file twice still fires.
                            e.target.value = '';
                          }}
                        />
                        {shownPhoto && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => imageInput.current?.click()}
                          >
                            {t('catalog.items.photo.replace')}
                          </Button>
                        )}
                      </>
                    )}
                    {shownPhoto && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setImageFile(null);
                          setImageCleared(true);
                        }}
                      >
                        {t('catalog.items.photo.remove')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {canEditExtras && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('catalog.items.modifierGroups')}</CardTitle>
                  <CardDescription>{t('catalog.items.groupsHint')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {attachedGroups.length ? (
                    <ul className="divide-y rounded-lg border">
                      {attachedGroups.map((group) => (
                        <li
                          key={group.id}
                          className="flex items-center justify-between gap-3 p-3 text-sm"
                        >
                          <span>
                            {localizedName(group, i18n.language)}
                            <span className="ms-2 text-ink-muted">
                              {t('catalog.modifiers.rule', {
                                min: group.minSelect,
                                max: group.maxSelect,
                              })}
                            </span>
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setGroupIds(groupIds.filter((id) => id !== group.id))}
                          >
                            {t('catalog.items.detach')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="rounded-lg border border-dashed border-line-strong p-4 text-center text-sm text-ink-muted">
                      {t('catalog.items.noGroups')}
                    </p>
                  )}
                  <Select
                    value=""
                    onChange={(e) => e.target.value && setGroupIds([...groupIds, e.target.value])}
                    aria-label={t('catalog.items.chooseGroup')}
                  >
                    <option value="">{t('catalog.items.chooseGroup')}</option>
                    {attachableGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {localizedName(g, i18n.language)}
                      </option>
                    ))}
                  </Select>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>
                  {t('catalog.items.availability')}
                </CardTitle>
              </CardHeader>
              <CardContent className="divide-y">
                {/* `isActive` is the only lifecycle flag the DTO carries. The
                    frame's other two switches have nothing to write: "Visible on
                    menu" is `MenuItem.isVisible`, a column on the menu↔item join
                    that an item on three menus holds three of, and "Allow
                    takeaway" does not exist anywhere — `FulfillmentType` is a
                    property of the Order. */}
                <SwitchRow
                  label={t('catalog.items.active')}
                  hint={t('catalog.items.activeHint')}
                  checked={form.isActive}
                  onChange={(isActive) => setForm({ ...form, isActive })}
                />
                {/* Sold out is a separate, separately audited route
                    (`PATCH /catalog/:id/sold-out`, `catalog_item.sold_out`), not
                    a synonym for inactive — so it writes immediately rather than
                    riding along with Save. It needs an item to write to. */}
                {editing && canUpdate && (
                  <SwitchRow
                    label={t('catalog.items.soldOut')}
                    hint={t('catalog.items.soldOutHint')}
                    checked={editing.isSoldOut}
                    onChange={() => void toggleSoldOut(editing)}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Dialog>

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          load();
          onChanged();
        }}
      />
    </div>
  );
}

/**
 * A checkbox that can also sit half-ticked.
 *
 * `indeterminate` is a DOM property, not an attribute, so React cannot set it
 * from JSX — without the ref the header box would show empty while some of the
 * page is selected, which reads as "nothing selected here" and is exactly the
 * wrong thing to say next to a Delete button.
 */
function CheckBox({
  checked,
  indeterminate,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer align-middle"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      title={label}
      ref={(node) => {
        if (node) node.indeterminate = !checked && !!indeterminate;
      }}
      onChange={onChange}
    />
  );
}

/**
 * One product row, wired for dragging.
 *
 * The handle is its own cell and its own button, sitting beside the selection
 * checkbox rather than replacing it: a tick and a drag are different intents,
 * and a row that started dragging when the operator meant to tick it would make
 * the bulk bar unusable on a touchscreen. The rest of the row — Edit, Delete,
 * the sold-out toggle — is untouched by the gesture for the same reason.
 */
function SortableItemRow({
  id,
  draggable,
  handleLabel,
  className,
  children,
}: {
  id: string;
  draggable: boolean;
  handleLabel: string;
  className?: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  return (
    <TR
      ref={setNodeRef}
      className={className}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        // A `<tr>` cannot be positioned, so the lifted row is set apart by
        // going translucent rather than by rising above its neighbours.
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      {children(
        <DragHandle
          ref={setActivatorNodeRef}
          label={handleLabel}
          dragging={isDragging}
          disabled={!draggable}
          {...attributes}
          {...listeners}
        />,
      )}
    </TR>
  );
}

/**
 * A titled block with a rule above it — the "Pricing" and "Kitchen routing"
 * dividers 64:205 draws down the left column. Not collapsible: unlike sizes and
 * modifier groups, every product has a price and a routing decision.
 */
function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

/**
 * A headed, collapsible block inside the item dialog.
 *
 * The dialog is already long, and sizes and modifier groups are the parts most
 * items never use — folded away they cost one line each, so the price and the
 * category stay above the fold. The summary line states what is inside, so a
 * closed section still says whether there is anything in it.
 */
function Section({
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 p-3 text-start"
      >
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-xs text-ink-muted">
          {summary} {open ? '▴' : '▾'}
        </span>
      </button>
      {open && <div className="space-y-3 border-t p-3">{children}</div>}
    </section>
  );
}
